import { get, isEmpty, intersection } from 'lodash'
import { pear as pearConstants } from '@edulastic/constants'
import {
  questerDefaultTools,
  ny3_8TestSkinGradeSubjectWiseToolsMap,
  ny3_8TestSkinGradeSubjectWiseMathToolsMap,
  TEST_SKIN_TYPE_NY_STATE_3_8,
} from './constants'

const { PA_GRADES_MAP, PA_SUBJECT_VALUES } = pearConstants

export const getApplicableToolsForTestSkin = ({
  subjects = [],
  grades = [],
  playerSkinType,
}) => {
  if (playerSkinType === TEST_SKIN_TYPE_NY_STATE_3_8) {
    const applicableTools = []
    const finalSubjects = (subjects || []).filter((subject) =>
      Object.keys(ny3_8TestSkinGradeSubjectWiseToolsMap).includes(subject)
    )

    if (isEmpty(finalSubjects) && isEmpty(grades)) {
      applicableTools.push(
        get(
          ny3_8TestSkinGradeSubjectWiseToolsMap,
          `${PA_SUBJECT_VALUES.MATHEMATICS}.grade.${PA_GRADES_MAP.GRADE_8.code}`,
          []
        )
      )
      Array.from(
        new Set([
          ...applicableTools.flat(),
          ...ny3_8TestSkinGradeSubjectWiseToolsMap.COMMON_TOOLS,
        ])
      )
    }

    if (finalSubjects.length === 0) {
      finalSubjects.push(PA_SUBJECT_VALUES.MATHEMATICS)
    }

    finalSubjects.forEach((subject) => {
      if (isEmpty(grades)) {
        if (subject === PA_SUBJECT_VALUES.MATHEMATICS) {
          applicableTools.push(
            get(
              ny3_8TestSkinGradeSubjectWiseToolsMap,
              `${subject}.grade.${PA_GRADES_MAP.GRADE_8.code}`,
              []
            )
          )
          return
        }
        if (subject === PA_SUBJECT_VALUES.SCIENCE) {
          applicableTools.push(
            get(
              ny3_8TestSkinGradeSubjectWiseToolsMap,
              `${subject}.grade.${PA_GRADES_MAP.GRADE_5.code}`,
              []
            )
          )
          return
        }
        if (subject === PA_SUBJECT_VALUES.ELA) {
          applicableTools.push(
            get(
              ny3_8TestSkinGradeSubjectWiseToolsMap,
              `${subject}.grade.allGrades`,
              []
            )
          )
          return
        }
      }

      grades.forEach((grade) => {
        if (
          subject === PA_SUBJECT_VALUES.ELA ||
          (subject === PA_SUBJECT_VALUES.SCIENCE &&
            [
              PA_GRADES_MAP.PRE_KINDERGARTEN.code,
              PA_GRADES_MAP.KINDERGARTEN.code,
              PA_GRADES_MAP.GRADE_1.code,
              PA_GRADES_MAP.GRADE_2.code,
              PA_GRADES_MAP.GRADE_3.code,
              PA_GRADES_MAP.GRADE_4.code,
            ].includes(grade)) ||
          (subject === PA_SUBJECT_VALUES.MATHEMATICS &&
            [
              PA_GRADES_MAP.PRE_KINDERGARTEN.code,
              PA_GRADES_MAP.KINDERGARTEN.code,
              PA_GRADES_MAP.GRADE_1.code,
              PA_GRADES_MAP.GRADE_2.code,
            ].includes(grade))
        ) {
          applicableTools.push(
            get(
              ny3_8TestSkinGradeSubjectWiseToolsMap,
              `${PA_SUBJECT_VALUES.ELA}.grade.allGrades`,
              []
            )
          )
          return
        }

        if (
          [
            PA_GRADES_MAP.GRADE_9.code,
            PA_GRADES_MAP.GRADE_10.code,
            PA_GRADES_MAP.GRADE_11.code,
            PA_GRADES_MAP.GRADE_11.code,
            PA_GRADES_MAP.GRADE_12.code,
            PA_GRADES_MAP.GRADE_13.code,
            PA_GRADES_MAP.OTHER.code,
          ].includes(grade)
        ) {
          grade = PA_GRADES_MAP.GRADE_8.code
        }

        applicableTools.push(
          get(
            ny3_8TestSkinGradeSubjectWiseToolsMap,
            `${subject}.grade.${grade}`,
            []
          )
        )
      })
    })

    return Array.from(
      new Set([
        ...applicableTools.flat(),
        ...ny3_8TestSkinGradeSubjectWiseToolsMap.COMMON_TOOLS,
      ])
    )
  }

  return questerDefaultTools
}

const getHighestGradeCode = (grades) => {
  if (
    isEmpty(grades) ||
    intersection(
      [
        PA_GRADES_MAP.GRADE_7.code,
        PA_GRADES_MAP.GRADE_8.code,
        PA_GRADES_MAP.GRADE_9.code,
        PA_GRADES_MAP.GRADE_10.code,
        PA_GRADES_MAP.GRADE_11.code,
        PA_GRADES_MAP.GRADE_12.code,
        PA_GRADES_MAP.GRADE_13.code,
        PA_GRADES_MAP.OTHER.code,
      ],
      grades
    )?.length > 0
  ) {
    return PA_GRADES_MAP.GRADE_8.code
  }

  if (
    intersection(
      [PA_GRADES_MAP.GRADE_5.code, PA_GRADES_MAP.GRADE_6.code],
      grades
    )?.length > 0
  ) {
    return PA_GRADES_MAP.GRADE_6.code
  }

  if (
    intersection(
      [PA_GRADES_MAP.GRADE_3.code, PA_GRADES_MAP.GRADE_4.code],
      grades
    )?.length > 0
  ) {
    return PA_GRADES_MAP.GRADE_4.code
  }

  return PA_GRADES_MAP.GRADE_2.code
}

export const getApplicableMathToolsForTestSkin = ({
  subjects = [],
  grades = [],
  isTestAttempt = false,
}) => {
  if (!isTestAttempt || (isEmpty(subjects) && isEmpty(grades))) {
    return get(
      ny3_8TestSkinGradeSubjectWiseMathToolsMap,
      `${PA_SUBJECT_VALUES.MATHEMATICS}.grade.${PA_GRADES_MAP.GRADE_8.code}`,
      []
    )
  }

  if (isEmpty(subjects)) {
    subjects.push(PA_SUBJECT_VALUES.MATHEMATICS)
  }

  if (subjects.includes(PA_SUBJECT_VALUES.MATHEMATICS)) {
    return get(
      ny3_8TestSkinGradeSubjectWiseMathToolsMap,
      `${PA_SUBJECT_VALUES.MATHEMATICS}.grade.${getHighestGradeCode(grades)}`,
      []
    )
  }

  return get(
    ny3_8TestSkinGradeSubjectWiseMathToolsMap,
    `${PA_SUBJECT_VALUES.SCIENCE}.grade.allGrades`,
    []
  )
}
